import React, { useState, useCallback, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { MessageSquarePlus, ThumbsUp, ThumbsDown, Camera, X, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/services/api';

const FeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rating, setRating] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasToken, setHasToken] = useState(false);

  // Check for token on mount and listen for storage changes
  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem('token');
      setHasToken(!!token);
    };
    
    // Initial check
    checkToken();
    
    // Listen for storage events (for cross-tab sync)
    window.addEventListener('storage', checkToken);
    
    // Also poll periodically since storage events don't fire in same tab
    const interval = setInterval(checkToken, 1000);
    
    return () => {
      window.removeEventListener('storage', checkToken);
      clearInterval(interval);
    };
  }, []);

  const reset = useCallback(() => {
    setTitle('');
    setDescription('');
    setRating(null);
    setScreenshot(null);
    setOpen(false);
  }, []);

  const captureScreenshot = useCallback(async () => {
    setCapturing(true);
    setOpen(false);
    await new Promise(r => setTimeout(r, 300));
    try {
      const canvas = await html2canvas(document.body, {
        scale: 0.5,
        useCORS: true,
        logging: false,
        ignoreElements: (el) => el.id === 'feedback-widget',
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
      setScreenshot(dataUrl);
      toast.success('Screenshot captured');
    } catch {
      toast.error('Screenshot failed');
    } finally {
      setCapturing(false);
      setOpen(true);
    }
  }, []);

  // Don't render if no token
  if (!hasToken) return null;

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error('Please add a title'); return; }
    if (!rating) { toast.error('Please select thumbs up or down'); return; }
    setSubmitting(true);
    try {
      await api.post('/feedback', {
        title: title.trim(),
        description: description.trim(),
        rating,
        page_url: window.location.pathname,
        screenshot,
      });
      toast.success('Thank you for your feedback!');
      reset();
    } catch {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="feedback-widget" className="fixed bottom-5 right-5 z-[9998]">
      {!open && (
        <Button
          onClick={() => setOpen(true)}
          className="rounded-full w-12 h-12 p-0 bg-indigo-600 hover:bg-indigo-700 shadow-lg"
          data-testid="feedback-open-btn"
        >
          <MessageSquarePlus className="w-5 h-5" />
        </Button>
      )}

      {open && (
        <Card className="w-80 shadow-2xl border border-gray-200 animate-in slide-in-from-bottom-2" data-testid="feedback-modal">
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-semibold">Send Feedback</CardTitle>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={reset}>
              <X className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {/* Rating */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">How's your experience?</span>
              <div className="flex gap-1 ml-auto">
                <Button
                  variant={rating === 'up' ? 'default' : 'outline'}
                  size="sm"
                  className={`h-8 w-8 p-0 ${rating === 'up' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                  onClick={() => setRating('up')}
                  data-testid="feedback-thumbs-up"
                >
                  <ThumbsUp className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant={rating === 'down' ? 'default' : 'outline'}
                  size="sm"
                  className={`h-8 w-8 p-0 ${rating === 'down' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  onClick={() => setRating('down')}
                  data-testid="feedback-thumbs-down"
                >
                  <ThumbsDown className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            {/* Title */}
            <Input
              placeholder="Title *"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="h-8 text-sm"
              data-testid="feedback-title"
            />

            {/* Description */}
            <Textarea
              placeholder="Describe your feedback..."
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              className="text-sm resize-none"
              data-testid="feedback-description"
            />

            {/* Screenshot */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={captureScreenshot}
                disabled={capturing}
                data-testid="feedback-screenshot-btn"
              >
                {capturing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                {screenshot ? 'Retake' : 'Screenshot'}
              </Button>
              {screenshot && (
                <div className="relative group">
                  <img src={screenshot} alt="screenshot" className="h-8 w-14 object-cover rounded border" />
                  <button
                    onClick={() => setScreenshot(null)}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center text-[8px] opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    x
                  </button>
                </div>
              )}
            </div>

            {/* Submit */}
            <Button
              className="w-full h-8 text-sm bg-indigo-600 hover:bg-indigo-700"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="feedback-submit-btn"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              {submitting ? 'Sending...' : 'Submit Feedback'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FeedbackButton;
